"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import FocusLock from 'react-focus-lock';
import { useId, useRef } from 'react';
import { validateSurface } from '../../globals.js';
import { Text } from '../../typography/text/index.js';
import { Portal } from '../../portal/portal/index.js';
import ScreenReaderText from '../../utilities/screen-reader-text/index.js';
import useViewport from '../../utilities/use-viewport/index.js';
import styles from './loader.module.scss.js';
import LoaderIndicator from '../../icons/component-icons/loader-indicator/index.js';

/**
 * @component Loader
 * @package Loader
 * @document ../_docs/code/Loader.editor.tsx
 * @document ../_docs/code/Loader.snippet.tsx
 * @document ../_docs/code/Loader.import.tsx
 */
const Loader = (_a) => {
    var { active = false, fullscreen = false, id, surface: surfaceProp = 'lightPrimary', size, textPosition = 'bottom', padding, showOverlay = true, text, indicatorColor } = _a, rest = __rest(_a, ["active", "fullscreen", "id", "surface", "size", "textPosition", "padding", "showOverlay", "text", "indicatorColor"]);
    const surface = validateSurface(surfaceProp);
    const spinnerStyles = [styles.spinner, styles[textPosition]];
    const overlayStyles = [styles.overlay, styles[surface.toLowerCase()]];
    const _id = useId();
    const idRef = useRef(null);
    const viewport = useViewport('standard');
    const ariaLabelValue = rest.ariaLabel || rest['aria-label'] || 'loader';
    function getPresetSize(loaderSize) {
        switch (loaderSize) {
            case 'xlarge':
                return {
                    iconSize: '48px',
                    kind: 'title',
                    textSize: 'small',
                    padding: 'var(--vds-space-6x)',
                };
            case 'large':
                return {
                    iconSize: '32px',
                    kind: 'body',
                    textSize: 'large',
                    padding: 'var(--vds-space-3x)',
                };
            case 'medium':
                return {
                    iconSize: '24px',
                    kind: 'body',
                    textSize: 'small',
                    padding: 'var(--vds-space-2x)',
                };
            case 'small':
                return {
                    iconSize: '16px',
                    kind: 'body',
                    textSize: 'small',
                    padding: 'var(--vds-space-2x)',
                };
            default:
                return {
                    iconSize: loaderSize,
                    kind: (text === null || text === void 0 ? void 0 : text.kind) || 'body',
                    textSize: (text === null || text === void 0 ? void 0 : text.size) || 'small',
                    padding: padding || 'var(--vds-space-2x)',
                };
        }
    }
    const getSize = () => {
        // Prefer explicit size if provided
        if (size)
            return getPresetSize(size);
        // Fallback to fullscreen logic
        if (fullscreen) {
            return getPresetSize(viewport === 'mobile' ? 'large' : 'xlarge');
        }
        // Default
        return getPresetSize('medium');
    };
    const _renderText = () => {
        const sizeConfig = getSize();
        const TextElement = (jsx(Text, Object.assign({ color: `var(--vds-color-element-primary-on${surface.toLowerCase()})` }, (text || {}), { primitive: (text === null || text === void 0 ? void 0 : text.primitive) || 'p', kind: sizeConfig.kind, size: sizeConfig.textSize })));
        if (!fullscreen)
            return TextElement;
        const halfIconSize = (() => {
            const iconSizeValue = String(sizeConfig.iconSize);
            const match = iconSizeValue.match(/^([\d.]+)([a-z%]*)$/i);
            /* istanbul ignore else -- @preserve */
            if (match) {
                const [, value, unit] = match;
                return `${parseFloat(value) / 2}${unit || 'px'}`;
            }
        })();
        const pos = `calc(50% + ${halfIconSize} + ${sizeConfig.padding})`;
        return (jsx("div", { className: styles.fullscreenText, style: textPosition === 'bottom' ? { top: pos } : { bottom: pos }, children: TextElement }));
    };
    const _renderLoader = () => {
        const sizeConfig = getSize();
        return (jsxs("div", { className: `${spinnerStyles.join(' ')}`, tabIndex: -1, role: "alert", "aria-label": `${ariaLabelValue} animation`, style: {
                '--gap': sizeConfig === null || sizeConfig === void 0 ? void 0 : sizeConfig.padding,
                '--indicator-color': indicatorColor ||
                    `var(--vds-color-element-primary-on${surface.toLowerCase()})`,
            }, children: [jsx(LoaderIndicator, { size: sizeConfig.iconSize, ariaHidden: true }), text && _renderText(), !showOverlay && (jsxs(ScreenReaderText, { children: [ariaLabelValue, " animation ", ariaLabelValue, " is in progress"] }))] }));
    };
    /* istanbul ignore else -- @preserve */
    if (!idRef.current) {
        idRef.current = id || _id;
    }
    if (active && typeof window !== 'undefined') {
        overlayStyles.push(styles.active);
        if (fullscreen) {
            overlayStyles.push(styles.fullscreenOverlay);
            return (jsx(Portal, { id: idRef.current, children: jsx(FocusLock, { children: jsx("div", { className: overlayStyles.join(' '), "aria-label": `${ariaLabelValue} in progress`, role: "alert", children: _renderLoader() }) }) }));
        }
        return showOverlay ? (jsx("div", { className: overlayStyles.join(' '), "aria-label": `${ariaLabelValue} in progress`, children: _renderLoader() })) : (_renderLoader());
    }
    return null;
};

export { Loader, Loader as default };
